import React from 'react';
import {
  Download,
  UserPlus,
  CreditCard,
  TrendingUp,
  CheckCircle,
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const HowItWorks = () => {
  const steps = [
    {
      icon: <Download className='w-8 h-8' />,
      title: '1. Baixe o App',
      description:
        'Faça o download gratuito do FinZiin e abra o controle das suas finanças.',
      color: 'bg-blue-500',
    },
    {
      icon: <UserPlus className='w-8 h-8' />,
      title: '2. Crie sua Conta',
      description:
        'Cadastro rápido, seguro e sem enrolação. Menos de 2 minutos para começar.',
      color: 'bg-emerald-500',
    },
    {
      icon: <CreditCard className='w-8 h-8' />,
      title: '3. Registre suas Contas',
      description:
        'Todos os bancos, cartões e investimentos em um único lugar. Gerencie tudo sem esforço.',
      color: 'bg-purple-500',
    },
    {
      icon: <TrendingUp className='w-8 h-8' />,
      title: '4. Domine suas Finanças',
      description:
        'Planeje, controle e conquiste metas. Não adie decisões, aja agora.',
      color: 'bg-orange-500',
    },
  ];

  const benefits = [
    'Comece a usar em menos de 5 minutos',
    'Interface intuitiva e fácil de entender',
    'Sincronização automática e segura de dados',
    'Suporte rápido direto pelo app',
    'Atualizações constantes com novas funcionalidades',
    'Seus dados sempre protegidos na nuvem e seguros',
  ];

  return (
    <section id='como-funciona' className='py-40 bg-gray-50'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Como o <span className='gradient-text'>FinZiin</span> funciona?
          </h2>
          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            Quatro passos simples e você está no comando do seu dinheiro.
          </p>
        </div>

        {/* Steps com animação ao scroll */}
        <div className='grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16 relative'>
          {steps.map((step, index) => {
            const { ref, inView } = useInView({
              threshold: 0.2,
              triggerOnce: false,
            });
            return (
              <div
                key={index}
                ref={ref}
                className={`text-center transition-all duration-700 transform ${
                  inView
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 ${step.color} text-white rounded-2xl mb-6 mx-auto shadow-lg`}
                >
                  {step.icon}
                </div>
                <h3 className='text-xl font-bold mb-3 text-gray-800'>
                  {step.title}
                </h3>
                <p className='text-gray-600 leading-relaxed'>
                  {step.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Benefits + CTA */}
        <div className='grid lg:grid-cols-2 gap-12 items-center'>
          <div>
            <h3 className='text-3xl font-bold mb-8 text-gray-800'>
              Por que escolher o FinZiin?
            </h3>
            <div className='space-y-4'>
              {benefits.map((benefit, index) => {
                const { ref, inView } = useInView({
                  threshold: 0.2,
                  triggerOnce: false,
                });
                return (
                  <div
                    key={index}
                    ref={ref}
                    className={`flex items-center space-x-4 transition-all duration-500 transform ${
                      inView
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-10'
                    }`}
                  >
                    <CheckCircle className='w-6 h-6 text-emerald-500' />
                    <span className='text-gray-700 text-lg'>{benefit}</span>
                  </div>
                );
              })}
            </div>

            <div className='mt-8'>
              <a
                href='https://play.google.com/store/apps/details?id=br.android.finziin'
                target='_blank'
                rel='noopener noreferrer'
                className='inline-flex items-center space-x-3 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transition-all'
              >
                <Download size={20} />
                <span>Começar Agora</span>
              </a>
            </div>
          </div>

          {/* Right Stats */}
          <div className='bg-white rounded-2xl p-8 shadow-xl'>
            <div className='text-center mb-8'>
              <h4 className='text-2xl font-bold text-gray-800 mb-2'>
                Faça parte da comunidade FinZiin
              </h4>
              <p className='text-gray-600'>
                Comece a controlar suas finanças de forma simples e segura
              </p>
            </div>
            <div className='grid grid-cols-3 gap-6'>
              <div className='text-center'>
                <div className='text-3xl font-bold gradient-text mb-2'>
                  500+
                </div>
                <div className='text-gray-600'>Usuários Ativos</div>
              </div>
              <div className='text-center'>
                <div className='text-3xl font-bold gradient-text mb-2'>
                  4.8⭐
                </div>
                <div className='text-gray-600'>Avaliação Média</div>
              </div>
              <div className='text-center'>
                <div className='text-3xl font-bold gradient-text mb-2'>
                  100%
                </div>
                <div className='text-gray-600'>Gratuito</div>
              </div>
            </div>
            <div className='mt-8 pt-6 border-t border-gray-200 flex justify-center space-x-4 text-sm text-gray-500'>
              <div className='flex items-center space-x-1'>
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                <span>Dados Criptografados</span>
              </div>
              <div className='flex items-center space-x-1'>
                <div className='w-2 h-2 bg-blue-500 rounded-full'></div>
                <span>LGPD Compliant</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
